import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, Text, StyleSheet, ScrollView, Image, Pressable,
} from 'react-native';
import { manipulateAsync } from 'expo-image-manipulator';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import {
  AssetCustomField, AssetInstance, MeterLabel, SensorLabel, Template, TemplateType, UserPermission,
} from '../../../../../../constants/Types';
import Colors from '../../../../../../constants/Colors';
import { Area, Floor, Space } from '../../../../../../components/Map/SetFloorsModal/FloorItem';
import {
  ADDITIONAL_FIELD_TYPES,
} from '../../../../../../components/AssetManagement/AddTemplateModal/components/FieldList/components/FieldItem';
import EssentialInfo from './components/EssentialInfo';
import LocationInfo from './components/LocationInfo';
import { screenMaxWidth } from '../../../../../../components/TopSection';
import Button from '../../../../../../components/Button';
import Style from '../../../../../../constants/Style';
import TabButton from '../../../../../../components/TabButton';
import { loadCustomFields } from '../../../../../../components/AssetManagement/data';
import { logEvent } from '../../../../../../components/LogEvent';
import uploadAssetImage from '../../../../../../requests/Assets/UploadAssetImage';
import { EDIT_ASSET_SUCCESS, IMAGE_UPLOAD_FAILURE, UPDATE_ASSET_FAILURE } from '../../../../../../constants/AlertMessages';
import isMobile from '../../../../../../components/IsMobile';
import updateAsset from '../../../../../../requests/Assets/UpdateAsset';
import BinIcon12 from '../../../../../../components/12pxIcons/BinIcon12';
import { Building } from '../../../../../LocationManagement/components/AddBuildingModal';
import { getDateComponents } from '../../../../../../constants/Utils';
import AssetAdditionalField from './components/AssetAdditionalField';

// eslint-disable-next-line no-shadow
export enum EditSectionSelection {
  // eslint-disable-next-line no-unused-vars
  ESSENTIAL = 'Essential', LOCATION = 'Location', ADDITIONAL = 'Additional'
}

const tabButtonTestIds: Record<EditSectionSelection, string> = {
  [EditSectionSelection.ESSENTIAL]: 'edit-asset-essential-btn',
  [EditSectionSelection.LOCATION]: 'edit-asset-location-btn',
  [EditSectionSelection.ADDITIONAL]: 'edit-asset-additional-btn',
};

interface EditAssetViewProps {
  asset: AssetInstance,
  template: Template,
  loading: boolean,
  setLoading: Dispatch<SetStateAction<boolean>>,
  showScreenAlert,
  setSelectedAsset: Dispatch<SetStateAction<AssetInstance>>,
  setEditViewShown: Dispatch<SetStateAction<boolean>>,
  onPressDeleteAsset: () => void,
  updateSelectedAsset: Dispatch<SetStateAction<AssetInstance>>,
}

const EditAssetView = ({
  asset, template, loading, setLoading, showScreenAlert, setEditViewShown, onPressDeleteAsset,
  updateSelectedAsset,
}: EditAssetViewProps) => {
  const navigation = useNavigation();
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditAsset: boolean = isAdmin || permissions.includes(UserPermission.ASSETS_UPDATE);
  const canDeleteAsset: boolean = isAdmin || permissions.includes(UserPermission.ASSETS_DELETE);
  const templateCustomFields: () => AssetCustomField[] = () => JSON.parse(
    JSON.stringify(
      loadCustomFields(template, asset, true)
        ?.sort((prev: AssetCustomField, next: AssetCustomField) => prev.name?.toLowerCase()
          .localeCompare(next.name?.toLowerCase()))
        .map((field) => ((field.value?.trim().length > 0
          && field.format === ADDITIONAL_FIELD_TYPES.DATE
          && new Date(field.value) instanceof Date
          && !Number.isNaN(new Date(field.value).getTime()))
          ? { ...field, value: Moment(field.value).format('DD/MM/YYYY') }
          : { ...field })),
    ),
  ) ?? [];
  const [selectedEditSection,
    setSelectedEditSection] = useState<EditSectionSelection>(EditSectionSelection.ESSENTIAL);
  const buildings: object = useSelector((state:
    { locations: { buildings: object } }) => state.locations.buildings);
  const [image, setImage] = useState<string>(asset.imageUrl);
  const [name, setName] = useState<string>(asset.name);
  const [description, setDescription] = useState<string>(asset.description);
  const [model, setModel] = useState<string>(asset.model);
  const [serial, setSerial] = useState<string>(asset.serial);
  const [manufacturer, setManufacturer] = useState<string>(asset.manufacturer);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [floorDropdownShown, setFloorDropdownShown] = useState<boolean>(false);
  const [areaDropdownShown, setAreaDropdownShown] = useState<boolean>(false);
  const [spaceDropdownShown, setSpaceDropdownShown] = useState<boolean>(false);
  const [floorInvalid, setFloorInvalid] = useState<boolean>(false);
  const [areaInvalid, setAreaInvalid] = useState<boolean>(false);
  const [spaceInvalid, setSpaceInvalid] = useState<boolean>(false);
  const [areasCanHaveAssets, setAreasCanHaveAssets] = useState<boolean>(true);
  const [spacesCanHaveAssets, setSpacesCanHaveAssets] = useState<boolean>(true);
  const [customFields, setCustomFields] = useState<AssetCustomField[]>(templateCustomFields());
  const [customFieldsValid,
    setCustomFieldsValid] = useState<boolean[]>(customFields?.map(() => true) ?? []);
  const [labels, setLabels] = useState<SensorLabel[] | MeterLabel[] | null>(
    [...(asset.labels
      ?.filter((label: SensorLabel | MeterLabel) => Object
        .values(asset.type === TemplateType.SENSOR ? SensorLabel : MeterLabel)
        .includes(label)) ?? [])] as SensorLabel[] | MeterLabel[],
  );
  const [labelsDropdownShown, setLabelsDropdownShown] = useState<boolean>(false);
  const [labelsInvalid, setLabelsInvalid] = useState<boolean>(false);
  const nonGeoFields: AssetCustomField[] = customFields
    ?.filter((field: AssetCustomField) => !(field?.name?.toLowerCase().includes('geo')));

  const handleImage = (uri: string) => {
    Image.getSize(uri, (w, h) => {
      manipulateAsync(uri, [{ resize: { height: Math.min(h, 1080) } }], {
        base64: true,
        compress: 0.3,
      }).then((m) => setImage(`data:image/jpeg;base64,${m.base64}`));
    });
  };

  useEffect(() => setLabelsInvalid(false), [labels]);

  useEffect(() => {
    let found: boolean = false;
    Object.keys(buildings ?? {}).forEach((buildingId: string) => {
      const building: Building = buildings[buildingId];
      if (found) return;
      if (buildingId === asset.locationId) {
        found = true;
        setSelectedBuilding(building);
        setSelectedFloor(null);
        setSelectedArea(null);
        setSelectedSpace(null);
      } else {
        Object.keys(building.floors ?? {}).forEach((floorId: string) => {
          const floor: Floor = building.floors[floorId];
          if (floorId === asset.locationId) {
            found = true;
            setSelectedBuilding(building);
            setSelectedFloor(floor);
            setSelectedArea(null);
            setSelectedSpace(null);
          } else {
            Object.keys(floor.areas ?? {}).forEach((areaId: string) => {
              const area: Area = floor.areas[areaId];
              if (areaId === asset.locationId) {
                found = true;
                setSelectedBuilding(building);
                setSelectedFloor(floor);
                setSelectedArea(area);
                setSelectedSpace(null);
              } else {
                Object.keys(area.spaces).forEach((spaceId: string) => {
                  const space: Space = area.spaces[spaceId];
                  if (spaceId === asset.locationId) {
                    found = true;
                    setSelectedBuilding(building);
                    setSelectedFloor(floor);
                    setSelectedArea(area);
                    setSelectedSpace(space);
                  }
                });
              }
            });
          }
        });
      }
    });
    const unsubscribe = navigation.addListener('blur', closeDropdowns);
    return unsubscribe;
  }, [asset.locationId]);

  useEffect(() => {
    setFloorInvalid(false);
    setFloorDropdownShown(false);
  }, [selectedFloor]);

  useEffect(() => {
    setAreaInvalid(false);
    setAreaDropdownShown(false);
  }, [selectedArea]);

  useEffect(() => {
    setSpaceInvalid(false);
    setSpaceDropdownShown(false);
  }, [selectedSpace]);

  const closeDropdowns: () => void = () => {
    setFloorDropdownShown(false);
    setAreaDropdownShown(false);
    setSpaceDropdownShown(false);
    setLabelsDropdownShown(false);
  };

  useEffect(closeDropdowns, [selectedEditSection]);

  useEffect(() => {
    setAreasCanHaveAssets(selectedBuilding?.Area?.IsAssetAvailable);
    setSpacesCanHaveAssets(selectedBuilding?.Space?.IsAssetAvailable);
  }, [selectedBuilding]);

  const save: () => void = () => {
    if (loading) return;
    closeDropdowns();
    setLoading(true);
    logEvent('EditAssetModal', 'Save');
    if (image !== asset.imageUrl) {
      uploadAssetImage(asset.id, image).then((result) => {
        if (result && !result.isError) {
          attemptUpdateAsset();
        } else showScreenAlert('Error', IMAGE_UPLOAD_FAILURE);
      }).finally(() => setLoading(false));
    } else attemptUpdateAsset();
  };

  const convertFields = (fields: AssetCustomField[]) => fields
    ?.map((field: AssetCustomField) => {
      if (field.value.trim()?.length > 0 && field.format === ADDITIONAL_FIELD_TYPES.DATE) {
        let date: Date = new Date(field.value.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$2/$1/$3'));
        if (isMobile) {
          const dateComponents: number[] = getDateComponents(field.value);
          date = new Date(dateComponents[2], dateComponents[1] - 1, dateComponents[0]);
        }
        if (date instanceof Date && !Number.isNaN(date.getTime())) {
          return ({ ...field, value: `${date?.toISOString()}` });
        }
        return field;
      }
      return field;
    });

  const validate: () => void = () => {
    logEvent('EditAssetModal', 'Save');
    const errorMessages: string[] = [];
    if (!(name?.trim().length > 0)) {
      if (!(errorMessages.length)) setSelectedEditSection(EditSectionSelection.ESSENTIAL);
      errorMessages.push('- Asset name cannot be empty');
    }
    if (asset.type !== TemplateType.ASSET && !(labels?.length)) {
      if (!(errorMessages.length)) setSelectedEditSection(EditSectionSelection.ESSENTIAL);
      errorMessages.push('- At least one label must be selected');
      setLabelsInvalid(true);
    }
    if (!selectedBuilding) {
      if (!(errorMessages.length)) setSelectedEditSection(EditSectionSelection.LOCATION);
      errorMessages.push('- A building must be selected');
    }
    if (!selectedFloor) {
      if (!(errorMessages.length)) setSelectedEditSection(EditSectionSelection.LOCATION);
      errorMessages.push('- A floor must be selected');
      setFloorInvalid(true);
    }
    if (!areasCanHaveAssets && spacesCanHaveAssets && selectedArea && !selectedSpace) {
      if (!(errorMessages.length)) setSelectedEditSection(EditSectionSelection.LOCATION);
      errorMessages.push('- Areas cannot have assets in this building');
      setAreaInvalid(true);
    }
    if (!spacesCanHaveAssets && selectedSpace) {
      if (!(errorMessages.length)) setSelectedEditSection(EditSectionSelection.LOCATION);
      errorMessages.push('- Spaces cannot have assets in this building');
      setSpaceInvalid(true);
    }
    const updatedCustomFieldsValid: boolean[] = [...customFieldsValid];
    convertFields(customFields)?.forEach((field: AssetCustomField, index: number) => {
      let isValid: boolean = customFieldsValid[index];
      if (isValid && field.format && field.value?.trim().length > 0
        && !new RegExp(field.format).test(field.value.trim())) {
        updatedCustomFieldsValid[index] = false;
        isValid = false;
      }
      if (!isValid) {
        const value: string = field.format === ADDITIONAL_FIELD_TYPES.DATE
          ? customFields[index].value : field.value;
        if (!(errorMessages.length)) setSelectedEditSection(EditSectionSelection.ADDITIONAL);
        errorMessages.push(`- Invalid value for ${field.name}: ${value}`);
      }
    });
    setCustomFieldsValid(updatedCustomFieldsValid);
    if (!(errorMessages.length)) save();
    else showScreenAlert('Error', errorMessages.join('\n'));
  };

  const attemptUpdateAsset: () => void = () => {
    const LocationId: string = selectedSpace?.LocationId ?? selectedArea?.LocationId
      ?? selectedFloor?.LocationId;
    const resetMapLocation: boolean = LocationId !== asset.locationId;
    const Name: string = name?.trim();
    const Manufacturer: string = manufacturer?.trim();
    const Description: string = description?.trim();
    const Model: string = model?.trim();
    const Serial: string = serial?.trim();
    const LocationBuilding: string = selectedBuilding?.Name?.trim();
    const LocationFloor: string = selectedFloor?.Name?.trim();
    const LocationArea: string = selectedArea?.Name?.trim();
    const LocationSpace: string = selectedSpace?.Name?.trim();
    const CustomFields: AssetCustomField[] = convertFields(customFields);
    if (resetMapLocation) {
      const geoXIndex = CustomFields.findIndex((field) => field.name === 'GeoX');
      const geoYIndex = CustomFields.findIndex((field) => field.name === 'GeoY');

      if (geoXIndex !== -1) {
        CustomFields[geoXIndex].value = '0';
      } else {
        CustomFields.push({ name: 'GeoX', value: '0' });
      }

      if (geoYIndex !== -1) {
        CustomFields[geoYIndex].value = '0';
      } else {
        CustomFields.push({ name: 'GeoY', value: '0' });
      }
    }
    updateAsset({
      assetId: asset.id,
      model: Model,
      serial: Serial,
      manufacturer: Manufacturer,
      description: Description,
      locationId: LocationId,
      customFields: CustomFields,
      name: Name,
      labels,
    }).then((response) => {
      const success = response && !response.isError;
      if (success) {
        updateSelectedAsset({
          ...asset,
          name: Name,
          model: Model,
          serial: Serial,
          description: Description,
          manufacturer: Manufacturer,
          locationId: LocationId,
          locationBuilding: LocationBuilding,
          locationFloor: LocationFloor,
          locationArea: LocationArea,
          locationSpace: LocationSpace,
          customFields: CustomFields,
          imageUrl: image,
          labels,
        });
        showScreenAlert('Success', EDIT_ASSET_SUCCESS, false);
        setEditViewShown(false);
      } else showScreenAlert('Error', UPDATE_ASSET_FAILURE);
    }).catch(() => {
      showScreenAlert('Error', UPDATE_ASSET_FAILURE);
    }).finally(() => setLoading(false));
  };

  return <Pressable
    onPress={closeDropdowns}
    style={styles.wrapper}
  >
    <View style={[styles.header, { maxWidth: screenMaxWidth - 16 }]}>
      <Text style={styles.screenTitle}>
        {asset?.name}
      </Text>
      <View style={Style.row}>
        {canDeleteAsset && <Button
          children={<BinIcon12 color={styles.deleteAssetButton.borderColor} />}
          onPress={onPressDeleteAsset}
          style={styles.deleteAssetButton}
          testID='edit-asset-delete-btn'
        />}
        <Button
          onPress={() => setEditViewShown(false)}
          title='Cancel'
          style={styles.saveButton}
          testID='edit-asset-cancel-btn'
        />
        {canEditAsset && <Button
          affirmative
          onPress={validate}
          title='Save'
          testID='edit-asset-save-btn'
          style={styles.saveButton}
        />}
      </View>
    </View>
    <View style={[styles.container, { maxWidth: screenMaxWidth }]}>
      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.title}>{selectedEditSection}</Text>
          <View style={Style.row}>
            {Object.values(EditSectionSelection)
              .map((editSection: EditSectionSelection) => <TabButton
                key={`tab_button_${editSection}`}
                title={editSection.toUpperCase()}
                select={() => setSelectedEditSection(editSection)}
                selected={selectedEditSection === editSection}
                testID={tabButtonTestIds[editSection]}
              />)}
          </View>
        </View>
        <ScrollView style={styles.scrollView}>
          {selectedEditSection === EditSectionSelection.ESSENTIAL && <EssentialInfo
            {...{
              name,
              setName,
              description,
              setDescription,
              model,
              setModel,
              manufacturer,
              setManufacturer,
              image,
              setImage,
              serial,
              setSerial,
              asset,
              labels,
              setLabels,
              labelsDropdownShown,
              setLabelsDropdownShown,
              labelsInvalid,
              handleImage,
            }}
          />}
          {selectedEditSection === EditSectionSelection.LOCATION && <LocationInfo
            {...{
              areasCanHaveAssets,
              spacesCanHaveAssets,
              selectedBuilding,
              selectedFloor,
              setSelectedFloor,
              selectedArea,
              setSelectedArea,
              selectedSpace,
              setSelectedSpace,
              floorDropdownShown,
              areaDropdownShown,
              spaceDropdownShown,
              floorInvalid,
              areaInvalid,
              spaceInvalid,
              setFloorDropdownShown,
              setAreaDropdownShown,
              setSpaceDropdownShown,
              asset,
              closeDropdowns,
            }}
          />}
          {selectedEditSection === EditSectionSelection.ADDITIONAL && <>
            {customFields?.map((field, index) => <AssetAdditionalField
              key={`additional_field_${index}`}
              field={field}
              index={index}
              customFieldsValid={customFieldsValid}
              setCustomFieldsValid={setCustomFieldsValid}
              customFields={customFields}
              setCustomFields={setCustomFields}
            />)}
            {!(nonGeoFields?.length > 0) && <View style={styles.noFieldsTextContainer}>
              <Text style={styles.noFieldsText}>{'No additional fields'}</Text>
            </View>}
          </>}
        </ScrollView>
      </View>
    </View>
  </Pressable>;
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    cursor: 'auto',
  },
  header: {
    ...Style.row,
    paddingBottom: 16,
  },
  container: {
    flexDirection: 'row',
    marginEnd: -16,
    flex: 1,
  },
  scrollView: {
    width: '50%',
    paddingEnd: 16,
  },
  content: {
    flex: 1,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    backgroundColor: '#FFF',
    borderRadius: 16,
    padding: 16,
    marginEnd: 24,
  },
  title: {
    fontSize: 20,
    lineHeight: 32,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  additionalField: {
    marginBottom: 16,
  },
  additionalFieldTitle: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Roboto_400Regular',
  },
  additionalFieldType: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    marginBottom: 8,
  },
  noFieldsTextContainer: {
    padding: 16,
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 10,
    marginBottom: 16,
    alignSelf: 'stretch',
  },
  noFieldsText: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  saveButton: {
    paddingHorizontal: 24,
    paddingVertical: 8,
    height: 38,
    marginStart: 8,
  },
  screenTitle: {
    flex: 1,
    fontSize: 32,
    lineHeight: 48,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginEnd: 16,
  },
  deleteAssetButton: {
    aspectRatio: 1,
    height: 40,
    paddingHorizontal: 0,
    borderColor: Colors.light.hereworksRed500,
    marginEnd: 8,
  },
});

export default EditAssetView;
